import UserDetails from '../ReusableComponents/UserDetails';
import ResponsiveTable from '../ReusableComponents/ResponsiveTable';
import MessageChat from '../ReusableComponents/MessageChat';
import VideoBrowser from '../ReusableComponents/VideoBrowser';
import MeetingRecordings from '../ReusableComponents/MeetingRecordings';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import AsyncSelect from 'react-select/async';
import Pagination from '@material-ui/lab/Pagination';
import { CircularProgress } from '@material-ui/core';
import moment from 'moment';
import Request from '../../Request';
import { logout } from '../../actions/AppActions';
import SectionButtons from '../ReusableComponents/SectionButtons';
import { MdMailOutline, MdMeetingRoom, MdAssignment } from 'react-icons/md';
import { RiArrowGoBackLine } from 'react-icons/ri';
import '../ReusableComponents/ModulePage.css';
import './ShowFiscal.css';

const sections = [
  { value: 'logs', label: 'Logs de acesso', description: 'Consulte os acessos e atividades do fiscal.', icon: 'logs' },
  { value: 'videos', label: 'Vídeos', description: 'Consulte os trechos gravados pelo fiscal nas chamadas.', icon: 'videos' },
  { value: 'recordings', label: 'Gravações', description: 'Assista às chamadas com os candidatos.', icon: 'videos' },
  { value: 'actions', label: 'Ações reportadas', description: 'Veja as ocorrências reportadas pelo fiscal.', icon: 'acoes' },
  { value: 'messages', label: 'Mensagens', description: 'Consulte o histórico das conversas com os candidatos.', icon: 'mensagens' },
];
const dateLabel = value => value ? moment(value).format('DD/MM/YYYY HH:mm:ss') : '—';
const initialSection = search => {
  const tab = new URLSearchParams(search).get('tab');
  return sections.some(item => item.value === tab) ? tab : 'logs';
};
const errorMessage = response => response?.message || Object.values(response?.errors || {}).flat().join(' ') || 'Não foi possível carregar os dados. Tente novamente.';

export default function ShowFiscal({ evaluator = false }) {
  const role = evaluator ? 'avaliador' : 'fiscal';
  const plural = evaluator ? 'avaliadores' : 'fiscais';
  const backTab = evaluator ? 9 : 6;
  const pageSections = evaluator ? sections.map(item => ({ ...item, description: item.description.replace(/fiscal/g, 'avaliador') })) : sections;
  const { id, processo_id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const section = initialSection(location.search);
  const selectedSection = pageSections.find(item => item.value === section);
  const [details, setDetails] = useState(null);
  const [headerError, setHeaderError] = useState('');
  const [headerRetry, setHeaderRetry] = useState(0);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [conversation, setConversation] = useState(null);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ items: [], pagination: { last_page: 1, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [mediaError, setMediaError] = useState(false);
  const requestId = useRef(0);
  const videoSelection = useRef('first');
  const previousSection = useRef(section);

  useEffect(() => {
    let active = true;
    setDetails(null);
    setHeaderError('');
    setPage(1); setSearch(''); setQuery('');
    setConversation(null);
    Request.Get(`faculdade/get_${role}_detalhes/${processo_id}/${id}`).then(response => {
      if (!active) return;
      if (response?.message === 'Unauthenticated.') { dispatch(logout()); return; }
      if (!(evaluator ? response?.avaliador : response?.fiscal)) { setHeaderError(errorMessage(response)); return; }
      setDetails(evaluator ? { ...response, fiscal: response.avaliador } : response);
    });
    return () => { active = false; };
  }, [id, processo_id, dispatch, headerRetry, evaluator, role]);

  useEffect(() => {
    const timer = setTimeout(() => { setQuery(search); setPage(1); }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (previousSection.current !== section) {
      previousSection.current = section;
      setSearch(''); setQuery(''); setPage(1);
      setConversation(null);
    }
  }, [section]);

  useEffect(() => {
    if (!details || section === 'recordings') return undefined;
    const sequence = ++requestId.current;
    if (section === 'messages' && !conversation) {
      setLoading(false); setError('');
      setResult({ items: [], pagination: { last_page: 1, total: 0 } });
      return undefined;
    }
    let active = true;
    setLoading(true); setError(''); setSelectedVideo(null); setMediaError(false);
    const params = new URLSearchParams({ search: query });
    if (section !== 'messages') params.set('page', String(page));
    if (section === 'messages') {
      params.set('conversation', conversation.value === 'general' ? 'general' : 'candidate');
      if (conversation.value !== 'general') params.set('candidato_id', conversation.value);
    }
    Request.Get(`faculdade/get_${role}_atividade/${processo_id}/${id}/${section}?${params.toString()}`).then(response => {
      if (!active || sequence !== requestId.current) return;
      if (response?.message === 'Unauthenticated.') { dispatch(logout()); return; }
      setLoading(false);
      if (!response?.items || (section !== 'messages' && !response?.pagination)) { setError(errorMessage(response)); return; }
      setResult({ ...response, pagination: response.pagination || { last_page: 1, total: response.items.length } });
      if (section === 'videos') {
        setSelectedVideo(response.items[videoSelection.current === 'last' ? response.items.length - 1 : 0] || null);
        videoSelection.current = 'first';
      }
    });
    return () => { active = false; };
  }, [details, id, processo_id, section, page, query, conversation?.value, retry, dispatch, role]);

  const recordingRequest = useCallback(async (path, method = 'GET') => {
    const response = method === 'POST' ? await Request.Post(path, new FormData()) : await Request.Get(path);
    if (response?.message === 'Unauthenticated.') dispatch(logout());
    return response;
  }, [dispatch]);
  const loadContacts = async input => {
    const response = await Request.Get(`faculdade/get_${role}_atividade/${processo_id}/${id}/contacts?search=${encodeURIComponent(input)}`);
    if (response?.message === 'Unauthenticated.') dispatch(logout());
    return [
      { value: 'general', label: 'Comunicados gerais' },
      ...(response?.items || []).map(user => ({ value: String(user.id), label: `${user.name}${user.numero_inscricao ? ` — ${user.numero_inscricao}` : ''}` })),
    ];
  };
  const changeSection = value => {
    const params = new URLSearchParams(location.search);
    params.set('tab', value);
    history.push({ search: `?${params.toString()}` });
  };
  const selectConversation = option => { setConversation(option); setPage(1); };

  if (headerError) return <div className="card"><div className="card-body">
    <div className="alert alert-danger" role="alert">{headerError}</div>
    <button className="btn btn-primary" onClick={() => setHeaderRetry(value => value + 1)}>Tentar novamente</button>{' '}
    <Link className="btn btn-secondary app-back-button" to={`/processos/show/${processo_id}?tab=${backTab}`}><RiArrowGoBackLine aria-hidden="true" /> Voltar</Link>
  </div></div>;
  if (!details) return <div className="card"><div className="card-body text-center" aria-label={`Carregando ${role}`}><CircularProgress /></div></div>;

  return <div className="module-page module-profile fiscal-details">
    <div className="card module-card module-identity"><div className="card-body">
      <div className="module-heading">
        <div className="module-avatar module-initials" aria-hidden="true">{details.fiscal.name?.trim().split(/\s+/).filter(Boolean).filter((_, index, names) => index === 0 || index === names.length - 1).map(name => name[0]).join('').toUpperCase()}</div>
        <div className="module-heading-text">
          <p className="module-identity-label">{evaluator ? 'Avaliador' : 'Fiscal'} · {details.processo.name}</p>
          <h1>{details.fiscal.name}</h1>
          <div className="module-meta"><MdMailOutline aria-hidden="true" /><span>{details.fiscal.email}</span></div>
        </div>
        <div className="module-back hidethis"><Link className="btn app-back-button" to={`/processos/show/${processo_id}?tab=${backTab}`}><RiArrowGoBackLine aria-hidden="true" /> Voltar aos {plural}</Link></div>
      </div>
      {!evaluator && <UserDetails user={details.fiscal} fiscal identity={false} />}
      <div className="module-facts">
        <div className="module-fact"><span className="module-fact-icon"><MdAssignment aria-hidden="true" /></span><div><span>Processo seletivo</span><strong>{details.processo.name}</strong></div></div>
        {!evaluator && <div className="module-fact"><span className="module-fact-icon is-cyan"><MdMeetingRoom aria-hidden="true" /></span><div><span>Salas vinculadas</span><strong>{details.salas.length ? details.salas.map(sala => `Sala ${sala.numero}`).join(' · ') : 'Sem sala vinculada'}</strong></div></div>}
      </div>
    </div></div>
    <div className="card module-card module-selector hidethis"><div className="card-body">
      <h2 className="module-selector-title hidethis">Escolha um módulo para visualizar</h2>
      <SectionButtons variant="modules" items={pageSections} value={section} onChange={changeSection} label={`Seções do ${role}`} />
    </div></div>
    <div className="card module-card module-content"><div className="card-body">
      <div className="module-content-heading hidethis"><h2>{selectedSection.label}</h2><p>{selectedSection.description}</p></div>
    {section === 'recordings' ? <MeetingRecordings interviewView={evaluator} endpoint={`faculdade/${role}_recordings/${processo_id}/${id}`} request={recordingRequest} /> : <section aria-label={selectedSection.label}>
      {section === 'logs' && <p className="text-muted">Acessos consecutivos agrupados por atividade, neste processo e antes da seleção do processo.</p>}
      <div className="fiscal-filters hidethis">
      {section === 'messages' && <div className="fiscal-conversation">
        <label htmlFor="fiscal-conversation">Conversa</label>
        <AsyncSelect key={`${processo_id}-${id}`} inputId="fiscal-conversation" classNamePrefix="fiscal-select" defaultOptions cacheOptions loadOptions={loadContacts}
          value={conversation} onChange={selectConversation} isClearable={false}
          placeholder="Selecione um candidato ou os comunicados gerais" noOptionsMessage={() => 'Nenhuma conversa encontrada'}
          loadingMessage={() => 'Buscando candidatos…'} />
        {conversation?.value === 'general' && <p className="text-muted mt-2">Comunicados enviados pelo {role} às salas deste processo e comunicados gerais da instituição.</p>}
      </div>}
      <label className="fiscal-search" htmlFor="fiscal-search">Buscar {section === 'messages' ? 'nas mensagens' : 'nos registros'}
        <input id="fiscal-search" type="search" className="form-control" value={search} maxLength={250}
          placeholder={section === 'messages' ? 'Digite um trecho da mensagem' : 'Digite para filtrar'} disabled={section === 'messages' && !conversation} onChange={event => setSearch(event.target.value)} />
      </label>
      </div>
      {section === 'messages' && !conversation ? <div className="fiscal-empty">Selecione um candidato para ver a conversa ou escolha os comunicados gerais.</div> : loading ? <div className="text-center p-4" role="status" aria-label="Carregando registros"><CircularProgress /></div> : error ?
        <div className="alert alert-danger" role="alert">{error} <button className="btn btn-sm btn-outline-danger" onClick={() => setRetry(value => value + 1)}>Tentar novamente</button></div> : <>
          {!result.items.length && <div className="fiscal-empty">Nenhum registro encontrado{query ? ' para esta busca' : ''}.</div>}
          {section === 'videos' && result.items.length > 0 && <VideoBrowser videos={result.items} selectedVideo={selectedVideo} page={page} lastPage={result.pagination.last_page} loading={loading} mediaError={mediaError} onMediaError={() => setMediaError(true)}
            onSelect={video => { setSelectedVideo(video); setMediaError(false); }} onPageChange={(value, selectLast) => { videoSelection.current = selectLast ? 'last' : 'first'; setPage(value); }} />}
          {result.items.length > 0 && section !== 'messages' && section !== 'videos' && <div className="table-responsive"><ResponsiveTable className="table table-striped">
            <thead><tr>{section === 'logs' ? <><th>Ação</th><th>De</th><th>Até</th><th>Contexto</th></> : <><th>Data e hora</th><th>Candidato</th><th>Ação reportada</th></>}</tr></thead>
            <tbody>{result.items.map(item => <tr key={item.id}>
              {section === 'logs' ? <>
                <td><span className="fiscal-text">{item.log}</span></td>
                <td>{dateLabel(item.started_at || item.created_at)}</td>
                <td>{dateLabel(item.ended_at || item.created_at)}</td>
                <td>{item.sala ? `Sala ${item.sala.numero}` : item.processo_id ? 'Neste processo' : 'Acesso geral'}</td>
              </> : <>
                <td>{dateLabel(item.date || item.created_at)}</td>
                <td>{(item.user || item.candidato)?.name || 'Candidato não identificado'}</td>
                <td><span className="fiscal-text">{item.acao}</span></td>
              </>}
            </tr>)}</tbody>
          </ResponsiveTable></div>}
          {section === 'messages' && <MessageChat messages={result.items} participantId={id} participantType="fiscal" candidateId={conversation.value === 'general' ? null : conversation.value} announcement={conversation.value === 'general'} />}
          {section !== 'messages' && result.pagination.total > 0 && <div className="fiscal-pagination"><span>{result.pagination.total} {section === 'logs' ? 'intervalos' : 'registros'} · Mais recentes primeiro</span>
            <Pagination className="hidethis" page={page} count={result.pagination.last_page} onChange={(_, value) => setPage(value)} /></div>}
        </>}
    </section>}
    </div></div>
  </div>;
}
